import styled from '@emotion/styled';

interface Document {
	name: string;
	url: string;
}

/* ── styled components ── */

const Section = styled.section`
	background-color: white;
	color: black;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-top: 1.5rem;
	padding-bottom: 1.5rem;
`;

const Heading = styled.div`
	text-transform: uppercase;
	font-weight: 500;
	font-size: 1.5rem;
	line-height: 2rem;
	letter-spacing: 0.3em;
	text-align: center;
	padding-top: 1.5rem;
	padding-bottom: 1.5rem;
	@media (min-width: 768px) {
		font-size: 2.25rem;
		line-height: 2.5rem;
	}
`;

const Grid = styled.div`
	display: flex;
	justify-content: space-around;
	width: 100%;
	gap: 1.5rem;
	padding-left: 1.5rem;
	padding-right: 1.5rem;
	border-top: 10px solid #e5e7eb;
`;

const DocumentItem = styled.div`
	padding-top: 1.5rem;
	padding-bottom: 1.5rem;
`;

const DocumentName = styled.div`
	font-size: 1.25rem;
	line-height: 1.75rem;
	font-weight: 500;
	width: 100%;
	text-align: center;
	padding-top: 1.5rem;
	padding-bottom: 1.5rem;
`;

const IconLink = styled.a`
	display: block;
	width: 6rem;
	height: 6rem;
	@media (min-width: 768px) {
		width: 12rem;
		height: 12rem;
	}
`;

/* ── PDF icon ── */

function PdfIcon() {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
			<path
				d="M14 14V4.5L9.5 0H4a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1 -1 1H4a1 1 0 0 1 -1 -1V2a1 1 0 0 1 1 -1h5.5z"
				strokeWidth="1"
			/>
			<path
				d="M4.603 14.087a0.8 0.8 0 0 1 -0.438 -0.42c-0.195 -0.388 -0.13 -0.776 0.08 -1.102 0.198 -0.307 0.526 -0.568 0.897 -0.787a7.7 7.7 0 0 1 1.482 -0.645 20 20 0 0 0 1.062 -2.227 7.3 7.3 0 0 1 -0.43 -1.295c-0.086 -0.4 -0.119 -0.796 -0.046 -1.136 0.075 -0.354 0.274 -0.672 0.65 -0.823 0.192 -0.077 0.4 -0.12 0.602 -0.077a0.7 0.7 0 0 1 0.477 0.365c0.088 0.164 0.12 0.356 0.127 0.538 0.007 0.188 -0.012 0.396 -0.047 0.614 -0.084 0.51 -0.27 1.134 -0.52 1.794a11 11 0 0 0 0.98 1.686 5.8 5.8 0 0 1 1.334 0.05c0.364 0.066 0.734 0.195 0.96 0.465 0.12 0.144 0.193 0.32 0.2 0.518 0.007 0.192 -0.047 0.382 -0.138 0.563a1.04 1.04 0 0 1 -0.354 0.416 0.86 0.86 0 0 1 -0.51 0.138c-0.331 -0.014 -0.654 -0.196 -0.933 -0.417a5.7 5.7 0 0 1 -0.911 -0.95 11.7 11.7 0 0 0 -1.997 0.406 11.3 11.3 0 0 1 -1.02 1.51c-0.292 0.35 -0.609 0.656 -0.927 0.787a0.8 0.8 0 0 1 -0.58 0.029m1.379 -1.901q-0.25 0.115 -0.459 0.238c-0.328 0.194 -0.541 0.383 -0.647 0.547 -0.094 0.145 -0.096 0.25 -0.04 0.361q0.016 0.032 0.026 0.044l0.035 -0.012c0.137 -0.056 0.355 -0.235 0.635 -0.572a8 8 0 0 0 0.45 -0.606m1.64 -1.33a13 13 0 0 1 1.01 -0.193 12 12 0 0 1 -0.51 -0.858 21 21 0 0 1 -0.5 1.05zm2.446 0.45q0.226 0.245 0.435 0.41c0.24 0.19 0.407 0.253 0.498 0.256a0.1 0.1 0 0 0 0.07 -0.015 0.3 0.3 0 0 0 0.094 -0.125 0.44 0.44 0 0 0 0.059 -0.2 0.1 0.1 0 0 0 -0.026 -0.063c-0.052 -0.062 -0.2 -0.152 -0.518 -0.209a4 4 0 0 0 -0.612 -0.053zM8.078 7.8a7 7 0 0 0 0.2 -0.828q0.046 -0.282 0.038 -0.465a0.6 0.6 0 0 0 -0.032 -0.198 0.5 0.5 0 0 0 -0.145 0.04c-0.087 0.035 -0.158 0.106 -0.196 0.283 -0.04 0.192 -0.03 0.469 0.046 0.822q0.036 0.167 0.09 0.346z"
				strokeWidth="1"
			/>
		</svg>
	);
}

/* ── component ── */

export default function DocumentBlockEta({ documents }: { documents: Document[] }) {
	if (documents.length === 0) return null;

	return (
		<Section id="documents">
			<Heading>Documents &amp; Brochures</Heading>
			<Grid>
				{documents.map((doc) => (
					<DocumentItem key={doc.url}>
						<DocumentName>{doc.name}</DocumentName>
						<IconLink href={doc.url} target="_blank" rel="nofollow">
							<PdfIcon />
						</IconLink>
					</DocumentItem>
				))}
			</Grid>
		</Section>
	);
}
